import * as React from "react"
import { Modal, Button, Container, Col, Row } from "react-bootstrap"
import { FaAngleDown } from "react-icons/fa"
import { AsyncTypeahead, Typeahead } from "react-bootstrap-typeahead"
import 'react-bootstrap-typeahead/css/Typeahead.css'
import { BehaviorSubject, of, concat, Subscription, combineLatest } from "rxjs"
import { debounceTime, filter, map, pairwise, startWith, switchMap } from "rxjs/operators"
import CompanyInput from "../FormattedInputs/CompanyInput"
import AutoFilterCompaniesGetObservable from "../../api/Companies/AutoFilterCompaniesGet"
import AutoFilterCompany from "../../api/Companies/CompanyModel/AutoFilterCompany"
import AutoFilterRegionsGetObservable from "../../api/Regions/AutoFilterRegionsGet"
import AutoFilterRegion from "../../api/Regions/Model/AutoFilterRegion"
import AutoFilterLocationsGetObservable from "../../api/Location/AutoFilterLocationsGet"
import AutoFilterLocation from "../../api/Location/LocationModel/AutoFilterLocation"
import LabServiceRegulatoryModesGetObservable from "../../api/LabService/LabServiceRegulatoryModesGet"
import RegulatoryMode from "../../api/LabService/LabServiceModel/RegulatoryMode"
import EmployeeCategoriesGetObservable from "../../api/LabService/EmployeeCategoriesGet"
import EmployeeCategory from "../../api/LabService/LabServiceModel/EmployeeCategory"

interface AdvancedFiltersModalProps {
  show: boolean
  selectedCompanies: AutoFilterCompany[]
  selectedRegions: AutoFilterRegion[]
  selectedLocations: AutoFilterLocation[]
  selectedRegulatoryModes: RegulatoryMode[]
  selectedCategories: EmployeeCategory[]
  accountType: string
  onSave: (companies: AutoFilterCompany[], regions: AutoFilterRegion[], locations: AutoFilterLocation[], regulatoryModes: RegulatoryMode[], categories: EmployeeCategory[], close: boolean) => void
  onClose: () => void
}

interface AdvancedFiltersModalState {
  companyTypeaheadKey: string
  unfilteredCompanies: AutoFilterCompany[]
  companies: AutoFilterCompany[]
  isCompaniesLoading: boolean
  selectedCompanies: AutoFilterCompany[]
  regionTypeaheadKey: string
  unfilteredRegions: AutoFilterRegion[]
  regions: AutoFilterRegion[]
  isRegionsLoading: boolean
  selectedRegions: AutoFilterRegion[]
  locationTypeaheadKey: string
  unfilteredLocations: AutoFilterLocation[]
  locations: AutoFilterLocation[]
  isLocationsLoading: boolean
  selectedLocations: AutoFilterLocation[]
  availableRegulatoryModes?: RegulatoryMode[]
  isRegulatoryModesLoading: boolean
  selectedRegulatoryModes: RegulatoryMode[]
  availableCategories?: EmployeeCategory[]
  isCategoriesLoading: boolean
  selectedCategories: EmployeeCategory[]
  canEditCompanies: boolean
  canEditRegions: boolean
  canEditLocations: boolean
}

export default class AdvancedFiltersModal extends React.Component<AdvancedFiltersModalProps, AdvancedFiltersModalState> {
  state: AdvancedFiltersModalState = {
    companyTypeaheadKey: "company",
    unfilteredCompanies: [],
    companies: [],
    isCompaniesLoading: false,
    selectedCompanies: this.props.selectedCompanies,
    regionTypeaheadKey: "region",
    unfilteredRegions: [],
    regions: [],
    isRegionsLoading: false,
    selectedRegions: this.props.selectedRegions,
    locationTypeaheadKey: "location",
    unfilteredLocations: [],
    locations: [],
    isLocationsLoading: false,
    selectedLocations: this.props.selectedLocations,
    isRegulatoryModesLoading: false,
    selectedRegulatoryModes: this.props.selectedRegulatoryModes,
    isCategoriesLoading: false,
    selectedCategories: [],
    canEditCompanies: ["master"].includes(this.props.accountType),
    canEditRegions: ["master", "company"].includes(this.props.accountType),
    canEditLocations: ["master", "company", "region"].includes(this.props.accountType)
  }

  subscriptions = new Subscription()

  searchCompany$ = new BehaviorSubject<string>("")
  searchRegion$ = new BehaviorSubject<string>("")
  searchLocation$ = new BehaviorSubject<string>("")

  selectedCompanies$ = new BehaviorSubject<AutoFilterCompany[]>([])
  selectedRegions$ = new BehaviorSubject<AutoFilterRegion[]>([])

  regionTypeahead = React.createRef<AsyncTypeahead<AutoFilterRegion>>()
  locationTypeahead = React.createRef<AsyncTypeahead<AutoFilterLocation>>()
  regulatoryModeTypeahead = React.createRef<Typeahead<RegulatoryMode>>()
  categoryTypeahead = React.createRef<Typeahead<EmployeeCategory>>()

  componentDidMount() {
    this.subscriptions.add(AutoFilterCompaniesGetObservable("", true).subscribe(
      (data: AutoFilterCompany[]) => {
        this.setState({
          unfilteredCompanies: data,
          isCompaniesLoading: false
        })
      },
      () => this.setState({ isCompaniesLoading: false })
    ))

    this.subscriptions.add(LabServiceRegulatoryModesGetObservable().subscribe(
      (data: RegulatoryMode[]) => {
        this.setState({
          availableRegulatoryModes: data,
          isRegulatoryModesLoading: false
        })
      }
    ))

    this.subscriptions.add(EmployeeCategoriesGetObservable().subscribe(
      (data: EmployeeCategory[]) => {
        this.setState({
          availableCategories: data,
          isCategoriesLoading: false
        })
      }
    ))

    type SearchRegionsData = { company: number, filter: string, companyChanged: boolean, shouldRequestApi: boolean, shouldSetEmptyResult: boolean, shouldUpdateUnfilteredList: boolean, shouldFillFromUnfilteredResult: boolean }

    this.subscriptions.add(combineLatest([
      this.selectedCompanies$,
      this.searchRegion$.pipe(debounceTime(500), map(text => text.trim())),
      of(this.state.canEditCompanies)
    ]).pipe(
      startWith([[], "", false]),
      pairwise(),
      map(([[prevCompanies, prevFilter, _1], [companies, filter, companiesAllowed]]) => {
        const prevC = prevCompanies as AutoFilterCompany[]
        const c = companies as AutoFilterCompany[]
        const filterLength = (filter as string).length
        if (companiesAllowed as boolean) {
          return {
            company: c.length === 1 ? c[0]?.AccountID || 0 : 0,
            filter: prevC.length !== c.length ? "" : filter,
            companyChanged: prevC.length !== c.length,
            shouldRequestApi: c.length === 1 && (prevC.length !== 1 || filterLength >= 3),
            shouldSetEmptyResult: c.length !== 1,
            shouldUpdateUnfilteredList: c.length === 1 && prevC.length !== 1,
            shouldFillFromUnfilteredResult: c.length === 1 && prevC.length === 1 && filterLength < 3
          } as SearchRegionsData
        } else {
          const isFirstTimeLoad = filterLength === 0 && (prevFilter as string).length === 0
          return {
            company: 0,
            filter: filter,
            companyChanged: false,
            shouldRequestApi: filterLength >= 3 || isFirstTimeLoad,
            shouldSetEmptyResult: false,
            shouldUpdateUnfilteredList: isFirstTimeLoad,
            shouldFillFromUnfilteredResult: filterLength < 3 && !isFirstTimeLoad
          } as SearchRegionsData
        }
      }),
      filter((o: SearchRegionsData) => o.shouldRequestApi || o.shouldFillFromUnfilteredResult || o.shouldSetEmptyResult),
      switchMap((o: SearchRegionsData) =>
        o.shouldFillFromUnfilteredResult || o.shouldSetEmptyResult
          ? of({ isLoading: false, shouldFillFromUnfilteredResult: o.shouldFillFromUnfilteredResult, shouldSetEmptyResult: o.shouldSetEmptyResult, companyChanged: o.companyChanged })
          : concat(
            of({ isLoading: true }),
            AutoFilterRegionsGetObservable(o.company, o.filter).pipe(
              map((data) => ({ isLoading: false, result: data, shouldUpdateUnfilteredList: o.shouldUpdateUnfilteredList }))
            )
          ))
    ).subscribe(
      (data: { isLoading: boolean, result?: AutoFilterRegion[], shouldUpdateUnfilteredList?: boolean, shouldFillFromUnfilteredResult?: boolean, shouldSetEmptyResult?: boolean, companyChanged?: boolean }) => {
        this.setState({
          regionTypeaheadKey: !!data.companyChanged ? Math.random().toString() : this.state.regionTypeaheadKey,
          isRegionsLoading: data.isLoading,
          unfilteredRegions: !!data.shouldUpdateUnfilteredList ? data.result || [] : this.state.unfilteredRegions,
          regions: !!data.shouldSetEmptyResult
            ? []
            : !!data.shouldFillFromUnfilteredResult
              ? this.state.unfilteredRegions
              : data.result || this.state.regions
        })
      }))

    type SearchLocationsData = { company: number, region: number, filter: string, regionChanged: boolean, shouldRequestApi: boolean, shouldSetEmptyResult: boolean, shouldUpdateUnfilteredList: boolean, shouldFillFromUnfilteredResult: boolean }

    this.subscriptions.add(combineLatest([
      this.selectedCompanies$,
      this.selectedRegions$,
      this.searchLocation$.pipe(debounceTime(500), map(text => text.trim())),
      of(this.state.canEditCompanies),
      of(this.state.canEditRegions)
    ]).pipe(
      startWith([[], [], "", false, false]),
      pairwise(),
      map(([[prevCompanies, prevRegions, prevFilter, _1, _2], [companies, regions, filter, companiesAllowed, regionsAllowed]]) => {
        const prevC = prevCompanies as AutoFilterCompany[]
        const c = companies as AutoFilterCompany[]
        const prevR = prevRegions as AutoFilterRegion[]
        const r = prevC.length !== c.length ? [] : regions as AutoFilterRegion[]
        const cAllowed = companiesAllowed as boolean
        const rAllowed = regionsAllowed as boolean
        const filterLength = (filter as string).length
        if (!rAllowed) {
          const isFirstTimeLoad = filterLength === 0 && (prevFilter as string).length === 0
          return {
            company: 0,
            region: 0,
            filter: filter,
            regionChanged: false,
            shouldRequestApi: filterLength >= 3 || isFirstTimeLoad,
            shouldSetEmptyResult: false,
            shouldUpdateUnfilteredList: isFirstTimeLoad,
            shouldFillFromUnfilteredResult: filterLength < 3 && !isFirstTimeLoad
          } as SearchLocationsData
        } else if (!cAllowed) {
          const isFirstTimeLoad = filterLength === 0 && (prevFilter as string).length === 0 && r.length === 0
          return {
            company: 0,
            region: r.length === 1 ? r[0]?.XrefAccountMapping?.RegionID || 0 : 0,
            filter: prevR.length !== r.length ? "" : filter,
            regionChanged: prevR.length !== r.length,
            shouldRequestApi: (r.length === 0 && prevR.length > 0) || (r.length === 1 && prevR.length !== 1) || filterLength >= 3 || isFirstTimeLoad,
            shouldSetEmptyResult: r.length > 1,
            shouldUpdateUnfilteredList: (r.length === 0 && prevR.length > 0) || (r.length === 1 && prevR.length !== 1) || isFirstTimeLoad,
            shouldFillFromUnfilteredResult: ((r.length === 0 && prevR.length === 0) || (r.length === 1 && prevR.length === 1)) && filterLength < 3 && !isFirstTimeLoad
          } as SearchLocationsData
        } else {
          return {
            company: c.length === 1 ? c[0]?.AccountID || 0 : 0,
            region: r.length === 1 ? r[0]?.XrefAccountMapping?.RegionID || 0 : 0,
            filter: prevC.length !== c.length || prevR.length !== r.length ? "" : filter,
            regionChanged: prevR.length !== r.length || prevC.length !== c.length,
            shouldRequestApi: (c.length === 1 && ((r.length === 0 && prevR.length > 0) || (r.length === 1 && prevR.length !== 1) || prevC.length !== 1 || filterLength >= 3)),
            shouldSetEmptyResult: r.length > 1,
            shouldUpdateUnfilteredList: (r.length === 0 && prevR.length > 0) || (r.length === 1 && prevR.length !== 1) || (c.length === 1 && prevC.length !== 1),
            shouldFillFromUnfilteredResult: ((r.length === 0 && prevR.length === 0) || (r.length === 1 && prevR.length === 1)) && (c.length === 1 && prevC.length === 1) && filterLength < 3
          } as SearchLocationsData
        }
      }),
      filter((o: SearchLocationsData) => o.shouldRequestApi || o.shouldFillFromUnfilteredResult || o.shouldSetEmptyResult || o.shouldFillFromUnfilteredResult),
      switchMap((o: SearchLocationsData) =>
        o.shouldFillFromUnfilteredResult || o.shouldSetEmptyResult
          ? of({ isLoading: false, shouldFillFromUnfilteredResult: o.shouldFillFromUnfilteredResult, shouldSetEmptyResult: o.shouldSetEmptyResult, regionChanged: o.regionChanged })
          : concat(
            of({ isLoading: true }),
            AutoFilterLocationsGetObservable(o.company, o.region, o.filter).pipe(
              map((data) => ({ isLoading: false, result: data, shouldUpdateUnfilteredList: o.shouldUpdateUnfilteredList, regionChanged: o.regionChanged }))
            )
          ))
    ).subscribe(
      (data: { isLoading: boolean, result?: AutoFilterLocation[], shouldUpdateUnfilteredList?: boolean, shouldFillFromUnfilteredResult?: boolean, shouldSetEmptyResult?: boolean, regionChanged?: boolean }) => {
        this.setState({
          locationTypeaheadKey: !!data.regionChanged ? Math.random().toString() : this.state.locationTypeaheadKey,
          isLocationsLoading: data.isLoading,
          unfilteredLocations: !!data.shouldUpdateUnfilteredList ? data.result || [] : this.state.unfilteredLocations,
          locations: !!data.shouldSetEmptyResult
            ? []
            : !!data.shouldFillFromUnfilteredResult
              ? this.state.unfilteredLocations
              : data.result || this.state.locations
        })
      }))

    this.subscriptions.add(this.searchCompany$.pipe(
      debounceTime(500),
      map(filter => (filter ?? "").trim()),
      filter(filter => filter.length >= 3 || filter.length === 0),
      switchMap((filter) => filter.length === 0
        ? of({ isLoading: false, items: this.state.unfilteredCompanies })
        : concat(
          of({ isLoading: true }),
          AutoFilterCompaniesGetObservable(filter).pipe(
            map((data) => ({ isLoading: false, items: data }))
          )
        ))
    ).subscribe(
      (state: { isLoading: boolean, items?: AutoFilterCompany[] }) => {
        state.items
          ? this.setState({ isCompaniesLoading: state.isLoading, companies: state.items })
          : this.setState({ isCompaniesLoading: state.isLoading })
      },
      () => this.setState({ isCompaniesLoading: false })
    ))
  }

  componentWillUnmount() {
    this.subscriptions.unsubscribe()
  }

  componentDidUpdate(prevProps: AdvancedFiltersModalProps, prevState: AdvancedFiltersModalState) {
    if (!(this.isCompaniesEqual(this.props.selectedCompanies, prevProps.selectedCompanies)
      && this.isRegionsEqual(this.props.selectedRegions, prevProps.selectedRegions)
      && this.isLocationsEqual(this.props.selectedLocations, prevProps.selectedLocations)
      && this.isRegulatoryModesEqual(this.props.selectedRegulatoryModes, prevProps.selectedRegulatoryModes)
      && this.isCategoriesEqual(this.props.selectedCategories, prevProps.selectedCategories))) {
      this.setState({
        selectedCompanies: this.props.selectedCompanies,
        selectedRegions: this.props.selectedRegions,
        selectedLocations: this.props.selectedLocations,
        selectedRegulatoryModes: this.props.selectedRegulatoryModes,
        selectedCategories: this.props.selectedCategories
      })
    } else {
      if (!this.isCompaniesEqual(prevState.selectedCompanies, this.state.selectedCompanies)) {
        this.searchCompany$.next("")
        this.selectedCompanies$.next(this.state.selectedCompanies)
      }
      if (!this.isRegionsEqual(prevState.selectedRegions, this.state.selectedRegions)) {
        this.selectedRegions$.next(this.state.selectedRegions)
      }
    }
  }

  isCompaniesEqual = (items1: AutoFilterCompany[], items2: AutoFilterCompany[]) => {
    const itemsString = (items: AutoFilterCompany[]) => items.map(item => item.AccountID).sort().join(",")
    return itemsString(items1) === itemsString(items2)
  }

  isRegionsEqual = (items1: AutoFilterRegion[], items2: AutoFilterRegion[]) => {
    const itemsString = (items: AutoFilterRegion[]) => items.map(item => item.Name).sort().join(",")
    return itemsString(items1) === itemsString(items2)
  }

  isLocationsEqual = (items1: AutoFilterLocation[], items2: AutoFilterLocation[]) => {
    const itemsString = (items: AutoFilterLocation[]) => items.map(item => item.Name).sort().join(",")
    return itemsString(items1) === itemsString(items2)
  }

  isRegulatoryModesEqual = (items1: RegulatoryMode[], items2: RegulatoryMode[]) => {
    const itemsString = (items: RegulatoryMode[]) => items.map(item => item.Name).sort().join(",")
    return itemsString(items1) === itemsString(items2)
  }

  isCategoriesEqual = (items1: EmployeeCategory[], items2: EmployeeCategory[]) => {
    const itemsString = (items: EmployeeCategory[]) => items.map(item => item.Name).sort().join(",")
    return itemsString(items1) === itemsString(items2)
  }

  handleSave = () => {
    const { selectedCompanies, selectedRegions, selectedLocations, selectedRegulatoryModes, selectedCategories } = this.state
    this.props.onSave(selectedCompanies, selectedRegions, selectedLocations, selectedRegulatoryModes, selectedCategories, true)
  }

  handleClearAll = () => {
    this.setState({ selectedCompanies: [], selectedRegions: [], selectedLocations: [], selectedRegulatoryModes: [], selectedCategories: [] })

    const { selectedCompanies, selectedRegions, selectedLocations, selectedRegulatoryModes, selectedCategories } = this.props
    const filterAlreadySet = selectedCompanies.length > 0 || selectedRegions.length > 0 || selectedLocations.length > 0 || selectedRegulatoryModes.length > 0 || selectedCategories.length > 0
    if (filterAlreadySet) {
      this.props.onSave([], [], [], [], [], false)
    }
  }

  handleClose = () => {
    this.setState({
      selectedCompanies: this.props.selectedCompanies,
      selectedRegions: this.props.selectedRegions,
      selectedLocations: this.props.selectedLocations,
      selectedRegulatoryModes: this.props.selectedRegulatoryModes,
      selectedCategories: this.props.selectedCategories
    })
    this.props.onClose()
  }

  handleAddCompany = (items: AutoFilterCompany[]) => {
    if (items[0] && !this.state.selectedCompanies.find(item => item.AccountID === items[0].AccountID)) {
      this.setState({
        companyTypeaheadKey: Math.random().toString(),
        selectedCompanies: [...this.state.selectedCompanies, items[0]],
        selectedRegions: [],
        selectedLocations: []
      })
    }
  }

  handleRemoveCompany = (removeItem: AutoFilterCompany) => {
    this.setState({
      companyTypeaheadKey: Math.random().toString(),
      selectedCompanies: this.state.selectedCompanies.filter(item => item.AccountID !== removeItem.AccountID)
    })
  }

  handleAddRegion = (items: AutoFilterRegion[]) => {
    if (items[0] && !this.state.selectedRegions.find(item => item.Name === items[0].Name)) {
      this.setState({
        regionTypeaheadKey: Math.random().toString(),
        selectedRegions: [...this.state.selectedRegions, items[0]],
        selectedLocations: []
      })
    }
  }

  handleRemoveRegion = (removeItem: AutoFilterRegion) => {
    this.setState({
      regionTypeaheadKey: Math.random().toString(),
      selectedRegions: this.state.selectedRegions.filter(item => item.Name !== removeItem.Name)
    })
  }

  handleAddLocation = (items: AutoFilterLocation[]) => {
    if (items[0] && !this.state.selectedLocations.find(item => item.Name === items[0].Name)) {
      this.setState({
        locationTypeaheadKey: Math.random().toString(),
        selectedLocations: [...this.state.selectedLocations, items[0]]
      })
    }
  }

  handleRemoveLocation = (removeItem: AutoFilterLocation) => {
    this.setState({
      locationTypeaheadKey: Math.random().toString(),
      selectedLocations: this.state.selectedLocations.filter(item => item.Name !== removeItem.Name)
    })
  }

  handleAddRegulatoryMode = (items: RegulatoryMode[]) => {
    if (items[0]) {
      this.setState({ selectedRegulatoryModes: [...this.state.selectedRegulatoryModes, items[0]] })
    }
  }

  handleRemoveRegulatoryMode = (removeItem: RegulatoryMode) => {
    this.setState({ selectedRegulatoryModes: this.state.selectedRegulatoryModes.filter(item => item.RegulatoryModeID !== removeItem.RegulatoryModeID) })
  }

  handleAddCategory = (items: EmployeeCategory[]) => {
    if (items[0]) {
      this.setState({ selectedCategories: [...this.state.selectedCategories, items[0]] })
    }
  }

  handleRemoveCategory = (removeItem: EmployeeCategory) => {
    this.setState({ selectedCategories: this.state.selectedCategories.filter(item => item.EmployeeCategoryID !== removeItem.EmployeeCategoryID) })
  }

  render() {
    // TODO: Get name from Account data once Auth/Account will be implemented
    const accountName = (): string => {
      switch (this.props.accountType) {
        case "company": return "Company"
        case "region": return "Region"
        case "location": return "Location"
      }
      return ""
    }

    const { selectedCompanies, selectedRegions, selectedLocations, selectedRegulatoryModes, selectedCategories,
      canEditCompanies, canEditRegions, canEditLocations } = this.state

    const showRegions = selectedCompanies.length === 1 || !canEditCompanies
    const showLocations = showRegions && selectedRegions.length <= 1
    const hasChanges = !(this.isCompaniesEqual(this.props.selectedCompanies, selectedCompanies)
      && this.isRegionsEqual(this.props.selectedRegions, selectedRegions)
      && this.isLocationsEqual(this.props.selectedLocations, selectedLocations)
      && this.isRegulatoryModesEqual(this.props.selectedRegulatoryModes, selectedRegulatoryModes)
      && this.isCategoriesEqual(this.props.selectedCategories, selectedCategories))
    const canClearAll = hasChanges || selectedCompanies.length > 0
      || selectedRegions.length > 0 || selectedLocations.length > 0
      || selectedRegulatoryModes.length > 0 || selectedCategories.length > 0

    return <Modal backdrop="static" size="xl" centered show={this.props.show} onHide={this.handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>ADVANCED FILTERS</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Container fluid className="AdvancedFiltersModal">
          {(!canEditCompanies || !canEditRegions || !canEditLocations) && <Row><Col md={6}>{accountName()}</Col></Row>}
          {canEditCompanies && <Row>
            <Col md={6}>
              <div className="typeahead">
                <CompanyInput onChange={this.handleAddCompany} placeholder="Company" />
              </div>
              <div className="selection">
                {this.state.selectedCompanies.map(item => <div key={item.AccountID}>
                  <span>{item.Name}</span>
                  <span className="remove" onClick={() => this.handleRemoveCompany(item)}>×</span>
                </div>)}
              </div>
            </Col>
          </Row>}
          <Row>
            {canEditRegions && <>
              <Col md={6} hidden={showRegions}></Col>
              <Col md={6} hidden={!showRegions}>
                <div className="typeahead">
                  <AsyncTypeahead id="region"
                    key={this.state.regionTypeaheadKey}
                    ref={this.regionTypeahead}
                    labelKey="Name"
                    filterBy={(option, props) => !this.state.selectedRegions.find(item => item.Name === option.Name)}
                    isLoading={this.state.isRegionsLoading}
                    onSearch={() => { }}
                    onInputChange={input => this.searchRegion$.next(input)}
                    onChange={this.handleAddRegion}
                    options={this.state.regions}
                    selected={[]}
                    minLength={0}
                    emptyLabel="No Result Found"
                    placeholder="Region"
                    inputProps={{ autoComplete: "chrome-off" }}>
                    {!this.state.isRegionsLoading &&
                      <Button variant="toggle" onClick={() => { this.regionTypeahead.current?.toggleMenu() }}>
                        <FaAngleDown />
                      </Button>}
                  </AsyncTypeahead>
                </div>
                <div className="selection">
                  {this.state.selectedRegions.map(item => <div key={item.Name}>
                    <span>{item.Name}</span>
                    <span className="remove" onClick={() => this.handleRemoveRegion(item)}>×</span>
                  </div>)}
                </div>
              </Col>
            </>}
            {canEditLocations && <>
              <Col md={6} hidden={showLocations}></Col>
              <Col md={6} hidden={!showLocations}>
                <div className="typeahead">
                  <AsyncTypeahead id="location"
                    key={this.state.locationTypeaheadKey}
                    ref={this.locationTypeahead}
                    labelKey="Name"
                    filterBy={(option, props) => !this.state.selectedLocations.find(item => item.Name === option.Name)}
                    isLoading={this.state.isLocationsLoading}
                    onSearch={() => { }}
                    onInputChange={input => this.searchLocation$.next(input)}
                    onChange={this.handleAddLocation}
                    options={this.state.locations}
                    selected={[]}
                    minLength={0}
                    emptyLabel="No Result Found"
                    placeholder="Location">
                    {!this.state.isLocationsLoading &&
                      <Button variant="toggle" onClick={() => { this.locationTypeahead.current?.toggleMenu() }}>
                        <FaAngleDown />
                      </Button>}
                  </AsyncTypeahead>
                </div>
                <div className="selection">
                  {this.state.selectedLocations.map(item => <div key={item.Name}>
                    <span>{item.Name}</span>
                    <span className="remove" onClick={() => this.handleRemoveLocation(item)}>×</span>
                  </div>)}
                </div>
              </Col>
            </>}
          </Row>
          <Row>
            <Col md={6}>
              <div className="typeahead">
                <Typeahead id="regMode"
                  ref={this.regulatoryModeTypeahead}
                  labelKey="Name"
                  filterBy={(option, props) => {
                    return props.selected.length > 0 || option.Name.toLowerCase().indexOf(props.text.toLowerCase()) !== -1;
                  }}
                  onChange={this.handleAddRegulatoryMode}
                  disabled={this.state.isRegulatoryModesLoading}
                  options={(this.state.availableRegulatoryModes || []).filter(item => !this.state.selectedRegulatoryModes.includes(item))}
                  selected={[]}
                  emptyLabel="No Result Found"
                  placeholder="Regulatory Mode">
                  <Button variant="toggle" onClick={() => { this.regulatoryModeTypeahead.current?.toggleMenu() }}>
                    <FaAngleDown />
                  </Button>
                </Typeahead>
              </div>
              <div className="selection">
                {this.state.selectedRegulatoryModes.map(item => <div key={item.RegulatoryModeID}>
                  <span>{item.Name}</span>
                  <span className="remove" onClick={() => this.handleRemoveRegulatoryMode(item)}>×</span>
                </div>)}
              </div>
            </Col>
            <Col md={6}>
              <div className="typeahead">
                <Typeahead id="category"
                  ref={this.categoryTypeahead}
                  labelKey="Name"
                  filterBy={(option, props) => {
                    return props.selected.length > 0 || option.Name.toLowerCase().indexOf(props.text.toLowerCase()) !== -1;
                  }}
                  onChange={this.handleAddCategory}
                  disabled={this.state.isCategoriesLoading}
                  options={(this.state.availableCategories || []).filter(item => !this.state.selectedCategories.includes(item))}
                  selected={[]}
                  emptyLabel="No Result Found"
                  placeholder="Category">
                  <Button variant="toggle" onClick={() => { this.categoryTypeahead.current?.toggleMenu() }}>
                    <FaAngleDown />
                  </Button>
                </Typeahead>
              </div>
              <div className="selection">
                {this.state.selectedCategories.map(item => <div key={item.EmployeeCategoryID}>
                  <span>{item.Name}</span>
                  <span className="remove" onClick={() => this.handleRemoveCategory(item)}>×</span>
                </div>)}
              </div>
            </Col>
          </Row>
        </Container>
      </Modal.Body>
      <Modal.Footer>
        {hasChanges && <Button variant="primary" onClick={() => this.handleSave()}>APPLY</Button>}
        {canClearAll && <Button variant="primary" onClick={() => this.handleClearAll()}>CLEAR ALL</Button>}
        {!hasChanges && <Button variant="secondary" onClick={() => this.handleClose()}>CLOSE</Button>}
      </Modal.Footer>
    </Modal >
  }
}
